/* OPS-30 Lash Refills - "The Restock Ritual" (19 July 2026)
   Vanilla JS, no dependencies. The moon fills as lashes are added.
   Adds go straight to the Shopify cart (/cart/add.js, /cart/change.js);
   the box state is re-hydrated from /cart.js on load. Box totals are
   computed from the REAL prices of the picked lashes: the deal price
   covers the most expensive picks and the total is never shown higher
   than the plain solo sum (Honest Pricing). All motion respects
   prefers-reduced-motion.

   28 July 2026: the hard 7 cap was removed. She can pick past the Full
   Moon; the meter flips to a calm "past full" state (dashed moon ring,
   overflow pips outside the box, honest maths: the box holds 7 at $99
   and extras ride at full price, exactly what the discount engine
   charges at 8+). View bag while past full opens the seatbelt modal
   with "trim back to seven" as the primary action. Same date: the
   "In my box" picks filter (#lr-picks-toggle) shows only her picked
   lashes so she can review and cull; any other filter interaction
   exits the picks view.

   Also 28 July 2026: the stepper went optimistic. Taps update the
   badge, moon and totals INSTANTLY from local state; a 450ms debounce
   coalesces rapid taps into one cart call per lash, and a serialized
   queue spaces the calls so Shopify's cart API stops 429ing. Displayed
   totals were always computed from the picked lashes' real prices, so
   nothing about Honest Pricing changes; only the sync confirmation is
   async. If a sync fails (after two retries) just that card is rolled
   back from cart truth and the calm error line shows. View bag and the
   confirm modal's continue link flush unsaved taps before navigating,
   so the bag always matches the box.

   15 August 2026 (cart-line race fix): quantity changes are now keyed
   on the cart line's stable `key`, never its 1-based index. Index
   targeting was racy: /cart/add.js inserts new items at line 1, so a
   liner or ritual add landing between a sync's read and its change
   shifted every line down and the change hit the WRONG line (observed:
   the Magic Liner silently deleted while its button still said "In
   your bag"). Same date: liner/ritual adds and removes now run through
   the same serialized cart queue as the lash steppers, so no two cart
   writes can ever interleave. */

(function () {
  'use strict';
  if (window.__lrRitualInit) return;
  window.__lrRitualInit = true;

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  onReady(function () {
    var gridSection = $('[data-lr-grid-section]');

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function readJSON(id, fallback) {
      var el = document.getElementById(id);
      if (!el) return fallback;
      try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
    }

    var cfg = readJSON('lr-config', {});
    cfg.smallCount = cfg.smallCount || 4;
    cfg.bigCount = cfg.bigCount || 7;
    cfg.smallPriceCents = cfg.smallPriceCents || 6900;
    cfg.bigPriceCents = cfg.bigPriceCents || 9900;
    var strings = readJSON('lr-strings', {});

    var NUM_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'];

    function numWord(n) {
      return (NUM_WORDS[n] || String(n));
    }

    function fmt(cents) {
      var d = cents / 100;
      return '$' + (Math.round(d * 100) % 100 === 0 ? Math.round(d) : d.toFixed(2));
    }

    /* ---------- registries ---------- */

    var cards = $$('.lrc-card').map(function (el) {
      return {
        el: el,
        variantId: el.getAttribute('data-variant-id'),
        price: parseInt(el.getAttribute('data-price'), 10) || 0,
        name: el.getAttribute('data-name') || '',
        handle: el.getAttribute('data-handle') || '',
        elig4: el.getAttribute('data-elig4') === 'true',
        elig7: el.getAttribute('data-elig7') === 'true',
        qty: 0
      };
    });
    var cardByVariant = {};
    cards.forEach(function (c) { cardByVariant[c.variantId] = c; });

    var banked = {};
    var boxes = 0;
    try {
      banked = JSON.parse(localStorage.getItem('lrBanked') || '{}') || {};
      boxes = parseInt(localStorage.getItem('lrBoxes') || '0', 10) || 0;
    } catch (e) { /* private mode: second-box state simply resets */ }

    function persistBank() {
      try {
        localStorage.setItem('lrBanked', JSON.stringify(banked));
        localStorage.setItem('lrBoxes', String(boxes));
      } catch (e) { /* ignore */ }
    }

    function upsells(selector, kind) {
      return $$(selector).map(function (el) {
        var addBtn = $(kind === 'liner' ? '[data-lr-liner-add]' : '[data-lr-ritual-add]', el);
        var chips = $$('[data-lr-variant-chip]', el);
        var priceEl = $(kind === 'liner' ? '[data-lr-liner-price]' : '[data-lr-ritual-price]', el);
        var selChip = chips.filter(function (ch) { return ch.getAttribute('aria-pressed') === 'true'; })[0];
        return {
          el: el,
          kind: kind,
          addBtn: addBtn,
          chips: chips,
          priceEl: priceEl,
          name: addBtn ? addBtn.getAttribute('data-name') : '',
          selectedId: selChip ? selChip.getAttribute('data-variant-id') : (addBtn ? addBtn.getAttribute('data-variant-id') : null),
          selectedPrice: parseInt((selChip || addBtn || { getAttribute: function () { return '0'; } }).getAttribute('data-price'), 10) || 0,
          selectedLabel: selChip ? selChip.getAttribute('data-label') : '',
          added: false,
          addedVariantId: null
        };
      });
    }

    var liners = upsells('[data-lr-liner]', 'liner');
    var rituals = upsells('[data-lr-ritual]', 'ritual');

    /* ---------- cart ---------- */

    function cartGet() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('cart');
        return r.json();
      });
    }
    function cartAdd(variantId, qty) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty || 1 }] })
      }).then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); });
    }
    function cartChange(payload) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) { if (!r.ok) throw new Error('change'); return r.json(); });
    }
    /* Quantity updates are keyed on the cart line's stable `key`, never its
       1-based index. The same variant can sit on more than one line
       (different selling plans or line item properties added elsewhere on
       the site) and an id-keyed change mis-targets those; an INDEX-keyed
       change is worse, because /cart/add.js inserts new items at line 1 and
       shifts every other line down mid-flight (the 15 Aug 2026 race that
       silently deleted a just-added liner). We read /cart.js, find the
       first line whose variant_id matches, and change that line by its key.
       If the variant is not in the cart at all we fall back to the id
       form. */
    function cartSetQty(variantId, qty) {
      return cartGet().then(function (cart) {
        var key = null;
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          if (String(items[i].variant_id) === String(variantId)) { key = items[i].key; break; }
        }
        return cartChange(key ? { id: key, quantity: qty } : { id: String(variantId), quantity: qty });
      });
    }

    var errorEl = $('[data-lr-error]');
    var errorTimer = null;
    function showError() {
      if (!errorEl) return;
      errorEl.textContent = strings.addError || 'That last change did not save. Please try again.';
      errorEl.hidden = false;
      clearTimeout(errorTimer);
      errorTimer = setTimeout(function () { errorEl.hidden = true; }, 4000);
    }

    /* ---------- box maths ---------- */

    /* Eligibility (OPS-57 pricing integrity): both box tiers count lashes in
       the pick-any-3-for-49 collection, the one source of truth the
       automatic discounts, the cart grouping and the cart banner all read.
       Eligibility is read from data-elig4 / data-elig7 (derived live from
       collection membership in Liquid), so it updates automatically when the
       collection changes. Lashes outside the collection are added to the
       cart at their real price, never discounted, and never advance the
       moon. */
    function boxCount() {
      // moon / waypoints / overflow track Full-Moon-eligible lashes only
      return cards.reduce(function (a, c) { return a + (c.elig7 ? c.qty : 0); }, 0);
    }
    /* boxTotal prices the BOX only: the eligible (Full-Moon) picks run through
       the deal maths (4 to 6 picks = $69 for the four dearest plus the extras
       at full solo price; 7 = $99; past 7 = $99 for the seven dearest plus
       the extras at full solo price, matching the discount engine, which
       applies one $75.93 reduction per order and charges everything beyond
       the box honestly). Ineligible cart items (legacy bulk-buys, lash+liner
       bundle variants, accessories, a leftover pre-existing cart) are NEVER
       summed in here, so they can never inflate the figure. Both the moon
       line and the View bag button show this box price.
       elig4 is counted independently of elig7: 4-for-$69 eligibility must
       never be gated on 7-for-$99 membership. */
    function boxTotal() {
      var boxPrices = [];
      var eligFour = 0;
      cards.forEach(function (c) {
        for (var i = 0; i < c.qty; i++) {
          if (c.elig7) {
            boxPrices.push(c.price);
          }
          if (c.elig4) eligFour += 1;
        }
      });
      var solo = boxPrices.reduce(function (a, b) { return a + b; }, 0);
      var n = boxPrices.length;
      var eligTotal;
      if (n >= cfg.bigCount) {
        boxPrices.sort(function (a, b) { return b - a; });
        var boxSum = boxPrices.slice(0, cfg.bigCount).reduce(function (a, b) { return a + b; }, 0);
        var beyond = boxPrices.slice(cfg.bigCount).reduce(function (a, b) { return a + b; }, 0);
        eligTotal = Math.min(Math.min(cfg.bigPriceCents, boxSum) + beyond, solo);
      } else if (eligFour >= cfg.smallCount) {
        boxPrices.sort(function (a, b) { return b - a; });
        var extras = boxPrices.slice(cfg.smallCount).reduce(function (a, b) { return a + b; }, 0);
        eligTotal = Math.min(cfg.smallPriceCents + extras, solo);
      } else {
        eligTotal = solo;
      }
      return eligTotal;
    }

    function pickedCount() {
      return cards.reduce(function (a, c) { return a + c.qty; }, 0);
    }

    /* ---------- moon ---------- */

    function moonPath(f) {
      if (f <= 0.001) return '';
      var rx = Math.abs(42 * Math.cos(Math.PI * f)).toFixed(2);
      var sweep = f < 0.5 ? 0 : 1;
      return 'M 50 8 A 42 42 0 0 1 50 92 A ' + rx + ' 42 0 0 ' + sweep + ' 50 8 Z';
    }

    /* ---------- render ---------- */

    var meter = {
      path: $('[data-lr-moon-path]'),
      charm: $('[data-lr-charm]'),
      maths: $('[data-lr-maths]'),
      pips: $('[data-lr-pips]'),
      checkout: $('[data-lr-checkout]'),
      second: $('[data-lr-second-box]'),
      note: $('[data-lr-boxes-note]'),
      root: $('[data-lr-meter]')
    };

    function render() {
      var n = boxCount();
      var total = boxTotal();
      var f = Math.min(n / cfg.bigCount, 1);
      var atCap = n >= cfg.bigCount;
      var over = Math.max(0, n - cfg.bigCount);
      var big = cfg.bigPriceCents;
      var small = cfg.smallPriceCents;

      if (meter.path) {
        meter.path.setAttribute('d', moonPath(f));
        if (atCap) {
          meter.path.setAttribute('fill', '#FDF0F4');
          meter.path.setAttribute('stroke', '#3D2535');
          meter.path.setAttribute('stroke-width', '2');
        } else {
          meter.path.setAttribute('fill', '#3D2535');
          meter.path.removeAttribute('stroke');
          meter.path.removeAttribute('stroke-width');
        }
      }
      if (meter.root) meter.root.classList.toggle('lrr-meter--over', over > 0);

      var charm = '';
      var maths = '';
      var toHalf = cfg.smallCount - n;
      var toFull = cfg.bigCount - n;
      if (n === 0) {
        charm = 'The moon is new. Choose your first lash.';
        maths = 'Any ' + cfg.smallCount + ' for ' + fmt(small) + ' · any ' + cfg.bigCount + ' for ' + fmt(big);
      } else if (n < cfg.smallCount) {
        charm = (toHalf === 1 ? 'One' : numWord(toHalf)) + ' more until half moon.';
        maths = n + ' chosen: ' + fmt(total) + ' · any ' + cfg.smallCount + ' for ' + fmt(small);
      } else if (n === cfg.smallCount) {
        charm = 'Half moon: ' + fmt(total) + '.';
        maths = toFull + ' more makes the Full Moon Box, ' + fmt(big) + ' all in.';
      } else if (n < cfg.bigCount) {
        charm = numWord(toFull) + ' more until full moon.';
        if (toFull === 1) {
          maths = total > big
            ? 'Your ' + n + ': ' + fmt(total) + ' · add 1 more and your total drops to ' + fmt(big) + '.'
            : 'Your ' + n + ': ' + fmt(total) + ' · any ' + cfg.bigCount + ' for ' + fmt(big) + '.';
        } else {
          var delta = big - total;
          maths = delta > 0
            ? 'Your ' + n + ': ' + fmt(total) + ' · ' + toFull + ' more lashes for ' + fmt(delta) + ' completes the Full Moon Box at ' + fmt(big) + '.'
            : 'Your ' + n + ': ' + fmt(total) + ' · any ' + cfg.bigCount + ' for ' + fmt(big) + '.';
        }
      } else if (n === cfg.bigCount) {
        charm = 'Your Full Moon Box is complete.';
        maths = cfg.bigCount + ' lashes. One full moon. ' + fmt(total) + '.';
      } else {
        var overWord = over === 1 ? 'one' : numWord(over).toLowerCase();
        charm = 'The moon is past full.';
        maths = 'Your ' + n + ': ' + fmt(total) + ' · the box holds ' + cfg.bigCount + ' for ' + fmt(big) + ', extras at full price. Set ' + overWord + ' aside to return to ' + fmt(big) + '.';
      }
      if (meter.charm) meter.charm.textContent = charm;
      if (meter.maths) meter.maths.textContent = maths;

      if (meter.pips) {
        var pipsHtml = '';
        for (var i = 0; i < cfg.bigCount; i++) {
          pipsHtml += '<span class="lrr-meter__pip' + (i < n ? ' lrr-meter__pip--filled' : '') + '"></span>';
        }
        for (var j = 0; j < over; j++) {
          pipsHtml += '<span class="lrr-meter__pip lrr-meter__pip--over"></span>';
        }
        meter.pips.innerHTML = pipsHtml;
      }

      if (meter.checkout) {
        // Shown as soon as ANYTHING is picked, eligible or not, so she can
        // always reach her bag. The figure is the box price (boxTotal).
        meter.checkout.hidden = pickedCount() === 0;
        meter.checkout.textContent = 'View bag · ' + fmt(total);
      }
      if (meter.second) meter.second.hidden = !atCap;
      if (meter.note) {
        meter.note.hidden = boxes === 0;
        meter.note.textContent = boxes === 1 ? '✦ Box 1 tucked in your bag' : '✦ ' + boxes + ' boxes tucked in your bag';
      }

      $$('[data-lr-waypoint]').forEach(function (wp) {
        var count = parseInt(wp.getAttribute('data-lr-waypoint'), 10);
        var isSmall = count === cfg.smallCount;
        var hi = isSmall ? n <= cfg.smallCount : n > cfg.smallCount;
        wp.classList.toggle('lrr-waypoint--active', hi);
        var reached = $('.lrr-waypoint__reached', wp);
        if (reached) reached.hidden = n < count;
      });

      cards.forEach(function (c) {
        var addBtn = $('[data-lr-add]', c.el);
        var stepper = $('[data-lr-stepper]', c.el);
        var badge = $('[data-lr-qty-badge]', c.el);
        var stepcount = $('[data-lr-stepcount]', c.el);
        var plus = $('[data-lr-plus]', c.el);
        c.el.classList.toggle('is-picked', c.qty > 0);
        if (badge) {
          badge.hidden = c.qty === 0;
          badge.textContent = c.qty;
        }
        if (stepper) stepper.hidden = c.qty === 0;
        if (addBtn) addBtn.hidden = c.qty > 0;
        if (stepcount) stepcount.textContent = c.qty + ' in box';
        if (plus) plus.disabled = false;
      });

      /* picks view upkeep: count on the toggle, auto-exit when the box
         empties, live re-filter so a culled lash leaves the view */
      var picked = pickedCount();
      if (picksBtn) {
        var pc = $('[data-lr-picks-count]', picksBtn);
        if (pc) pc.textContent = picked;
        if (filterState.picks && picked === 0) setPicks(false);
        picksBtn.hidden = picked === 0;
        if (filterState.picks) applyFilters();
      }

      liners.concat(rituals).forEach(renderUpsell);
    }

    function renderUpsell(u) {
      if (u.priceEl) u.priceEl.textContent = fmt(u.selectedPrice);
      if (!u.addBtn) return;
      u.addBtn.classList.toggle('is-added', u.added);
      if (u.added) {
        u.addBtn.textContent = u.kind === 'liner' ? 'In your bag ✦ Remove' : 'In your order ✦ Remove';
      } else if (u.kind === 'liner') {
        u.addBtn.textContent = 'Add liner · ' + (u.selectedLabel ? u.selectedLabel + ' · ' : '') + fmt(u.selectedPrice);
      } else {
        u.addBtn.textContent = 'Add to order';
      }
    }

    /* ---------- star + nudge helpers ---------- */

    function fireStar(card) {
      if (reduceMotion) return;
      var star = $('[data-lr-star]', card.el);
      if (!star) return;
      star.hidden = false;
      star.classList.remove('is-firing');
      void star.offsetWidth;
      star.classList.add('is-firing');
      setTimeout(function () { star.hidden = true; star.classList.remove('is-firing'); }, 400);
    }

    function nudgeMeter() {
      if (meter.root) meter.root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }

    /* ---------- mutations (optimistic) ---------- */

    /* pending still guards the liner/ritual upsell buttons (single taps,
       unchanged); the lash stepper no longer uses it. */
    var pending = false;

    /* The stepper updates local state and re-renders IMMEDIATELY, then a
       450ms per-lash debounce coalesces her taps into one cart call. All
       cart calls run through one serialized queue: Shopify throttles
       concurrent cart writes (429), and one-at-a-time with coalescing
       stays comfortably under the limit. `known` is the last confirmed
       cart quantity per variant (hydrated on load); a sync is skipped
       when the cart already matches. Liner/ritual adds run through the
       SAME queue (15 Aug 2026), so no cart write ever interleaves with
       another. */
    var syncQueue = Promise.resolve();
    var pendingSyncs = 0;
    var dirty = {};
    var syncTimers = {};
    var known = {};
    var touched = {};
    var SYNC_DELAY = 450;

    function delay(ms) {
      return new Promise(function (resolve) { setTimeout(resolve, ms); });
    }

    function desiredQty(variantId) {
      var card = cardByVariant[variantId];
      return (banked[variantId] || 0) + (card ? card.qty : 0);
    }

    function scheduleSync(variantId) {
      dirty[variantId] = true;
      clearTimeout(syncTimers[variantId]);
      syncTimers[variantId] = setTimeout(function () { enqueueSync(variantId); }, SYNC_DELAY);
    }

    function enqueueSync(variantId) {
      if (!dirty[variantId]) return;
      dirty[variantId] = false;
      pendingSyncs++;
      syncQueue = syncQueue
        .then(function () { return syncVariant(variantId, 0); })
        .then(function () { pendingSyncs--; }, function () { pendingSyncs--; });
    }

    /* Run any one-off cart write through the serialized queue. Returns a
       promise for that write alone; the queue itself never rejects. */
    function enqueueCartOp(fn) {
      pendingSyncs++;
      var run = syncQueue.then(fn);
      syncQueue = run.then(function () { pendingSyncs--; }, function () { pendingSyncs--; });
      return run;
    }

    function syncVariant(variantId, attempt) {
      var want = desiredQty(variantId);
      if (want === (known[variantId] || 0)) return Promise.resolve();
      var op = (known[variantId] || 0) === 0 && want > 0
        ? cartAdd(variantId, want)
        : cartSetQty(variantId, want);
      return op.then(function () {
        known[variantId] = want;
        // she kept tapping while this was in flight: go again
        if (desiredQty(variantId) !== want) return syncVariant(variantId, 0);
      }, function () {
        if (attempt < 2) {
          return delay(attempt === 0 ? 1500 : 3500).then(function () {
            return syncVariant(variantId, attempt + 1);
          });
        }
        // out of retries: put just this card back the way the cart has it
        return cartGet().then(function (cart) {
          var cq = 0;
          cart.items.forEach(function (it) {
            if (String(it.variant_id) === String(variantId)) cq += it.quantity;
          });
          known[variantId] = cq;
          var card = cardByVariant[variantId];
          if (card) card.qty = Math.max(0, cq - (banked[variantId] || 0));
          render();
          showError();
        }, function () { showError(); });
      });
    }

    function syncsBusy() {
      if (pendingSyncs > 0) return true;
      for (var k in dirty) { if (dirty[k]) return true; }
      return false;
    }

    function flushSyncs() {
      for (var v in syncTimers) { clearTimeout(syncTimers[v]); }
      for (var k in dirty) { if (dirty[k]) enqueueSync(k); }
      return syncQueue;
    }

    /* Before leaving for the cart, land any unsaved taps so the bag she
       sees matches the box she built. The 4s timeout means a bad
       connection can never trap her on this page. */
    function flushThenNavigate(e, el) {
      if (!syncsBusy()) return;
      e.preventDefault();
      var href = el.getAttribute('href') || '/cart';
      var done = false;
      function go() { if (!done) { done = true; window.location.href = href; } }
      flushSyncs().then(go, go);
      setTimeout(go, 4000);
    }

    function addOne(card) {
      card.qty += 1;
      touched[card.variantId] = true;
      fireStar(card);
      render();
      scheduleSync(card.variantId);
    }

    function removeOne(card) {
      if (card.qty === 0) return;
      card.qty -= 1;
      touched[card.variantId] = true;
      render();
      scheduleSync(card.variantId);
    }

    /* ---------- hydrate from cart ---------- */

    function hydrate() {
      return cartGet().then(function (cart) {
        var qtyByVariant = {};
        cart.items.forEach(function (it) {
          qtyByVariant[String(it.variant_id)] = (qtyByVariant[String(it.variant_id)] || 0) + it.quantity;
        });
        if (cart.item_count === 0) { banked = {}; boxes = 0; }
        Object.keys(banked).forEach(function (k) {
          banked[k] = Math.min(banked[k], qtyByVariant[k] || 0);
          if (!banked[k]) delete banked[k];
        });
        persistBank();
        cards.forEach(function (c) {
          var cq = qtyByVariant[c.variantId] || 0;
          known[c.variantId] = cq;
          /* never stomp a card she has already tapped: her local intent
             wins and the scheduled sync reconciles the cart to it */
          if (!touched[c.variantId]) {
            c.qty = Math.max(0, cq - (banked[c.variantId] || 0));
          }
        });
        liners.concat(rituals).forEach(function (u) {
          var ids = u.chips.length
            ? u.chips.map(function (ch) { return ch.getAttribute('data-variant-id'); })
            : (u.selectedId ? [u.selectedId] : []);
          u.added = false;
          u.addedVariantId = null;
          ids.forEach(function (id) {
            if (qtyByVariant[id]) {
              u.added = true;
              u.addedVariantId = id;
            }
          });
          if (u.added && u.addedVariantId && u.chips.length) {
            u.chips.forEach(function (ch) {
              var on = ch.getAttribute('data-variant-id') === u.addedVariantId;
              ch.setAttribute('aria-pressed', String(on));
              if (on) {
                u.selectedId = u.addedVariantId;
                u.selectedPrice = parseInt(ch.getAttribute('data-price'), 10) || u.selectedPrice;
                u.selectedLabel = ch.getAttribute('data-label') || '';
              }
            });
          }
        });
        render();
      }).catch(function () { render(); });
    }

    /* ---------- filters ---------- */

    var filterState = { style: null, type: null, eye: null, glasses: null, q: '', picks: false };
    var lastFilterChanged = null;
    var countEl = document.getElementById('lr-result-count');
    var clearBtn = document.getElementById('lr-clear-filters');
    var zeroEl = document.getElementById('lr-zero');
    var zeroMsg = document.getElementById('lr-zero-msg');
    var zeroUndo = document.getElementById('lr-zero-undo');
    var findInput = document.getElementById('lr-find-input');
    var eyeDisclosure = document.getElementById('lr-eye-disclosure');
    var picksBtn = document.getElementById('lr-picks-toggle');
    var filtersSection = $('.lrc-filters');

    function cardMatches(c) {
      /* Picks view: show exactly what is in her box, whatever the other
         filters say. She is here to review and cull, not to search. */
      if (filterState.picks) return c.qty > 0;
      var el = c.el;
      if (filterState.style && el.getAttribute('data-look') !== filterState.style) return false;
      if (filterState.type) {
        var ranges = (el.getAttribute('data-range') || '').split('|');
        if (ranges.indexOf(filterState.type) === -1) return false;
      }
      if (filterState.eye) {
        if (el.getAttribute('data-eye-complete') !== 'true') return false;
        var eyes = (el.getAttribute('data-eye') || '').split('|');
        if (eyes.indexOf(filterState.eye) === -1) return false;
      }
      if (filterState.glasses && el.getAttribute('data-glasses') !== 'true') return false;
      if (filterState.q) {
        var q = filterState.q.toLowerCase();
        var name = (c.name || '').toLowerCase();
        var title = el.getAttribute('data-title') || '';
        if (name.indexOf(q) === -1 && title.indexOf(q) === -1) return false;
      }
      return true;
    }

    function anyFilter() {
      return !!(filterState.style || filterState.type || filterState.eye || filterState.glasses || filterState.q);
    }

    function applyFilters() {
      var visible = 0;
      cards.forEach(function (c) {
        var ok = cardMatches(c);
        c.el.classList.toggle('is-hidden', !ok);
        if (ok) visible++;
      });
      if (countEl) {
        countEl.textContent = filterState.picks
          ? (strings.showingPicks || 'Showing the [n] in your box').replace('[n]', visible)
          : (strings.showing || 'Showing [n] of [total] lashes')
              .replace('[n]', visible)
              .replace('[total]', cards.length);
      }
      if (clearBtn) clearBtn.classList.toggle('is-visible', anyFilter() && !filterState.picks);
      if (zeroEl) {
        var showZero = visible === 0 && cards.length > 0 && !filterState.picks;
        zeroEl.classList.toggle('is-visible', showZero);
        if (showZero) {
          if (lastFilterChanged) {
            zeroMsg.textContent = (strings.zero || '').replace('[filter]', lastFilterChanged.label);
            zeroUndo.textContent = (strings.zeroUndo || '').replace('[filter]', lastFilterChanged.label);
            zeroUndo.hidden = false;
          } else {
            zeroMsg.textContent = strings.zeroPlain || '';
            zeroUndo.hidden = true;
          }
        }
      }
      updateHash();
    }

    /* ---------- picks view ("In my box") ---------- */

    function setPicks(on) {
      filterState.picks = !!on;
      if (picksBtn) picksBtn.setAttribute('aria-pressed', String(filterState.picks));
      if (filtersSection) filtersSection.classList.toggle('lr-picks-active', filterState.picks);
      applyFilters();
    }

    /* Any ordinary filter interaction quietly leaves the picks view; her
       chip and search state was never cleared, so it simply resumes. */
    function exitPicks() {
      if (!filterState.picks) return;
      filterState.picks = false;
      if (picksBtn) picksBtn.setAttribute('aria-pressed', 'false');
      if (filtersSection) filtersSection.classList.remove('lr-picks-active');
    }

    if (picksBtn) picksBtn.addEventListener('click', function () {
      setPicks(!filterState.picks);
    });

    function updateHash() {
      var parts = [];
      if (filterState.style) parts.push('style=' + filterState.style);
      if (filterState.type) parts.push('type=' + filterState.type);
      if (filterState.eye) parts.push('eye=' + filterState.eye);
      if (filterState.glasses) parts.push('glasses=1');
      if (filterState.q) parts.push('q=' + encodeURIComponent(filterState.q));
      var hash = parts.length ? '#' + parts.join('&') : (location.pathname + location.search);
      try { history.replaceState(null, '', hash); } catch (e) { /* ignore */ }
    }

    function parseHash() {
      var raw = location.hash.replace(/^#/, '');
      if (!raw) return;
      raw.split('&').forEach(function (kv) {
        var p = kv.split('=');
        if (p[0] === 'style') filterState.style = p[1];
        if (p[0] === 'type') filterState.type = p[1];
        if (p[0] === 'eye') filterState.eye = p[1];
        if (p[0] === 'glasses') filterState.glasses = '1';
        if (p[0] === 'q') filterState.q = decodeURIComponent(p[1] || '');
      });
      $$('.lrc-chip[data-filter-group]').forEach(function (chip) {
        var g = chip.getAttribute('data-filter-group');
        var v = chip.getAttribute('data-filter-value');
        var on = (g === 'glasses' && filterState.glasses) || filterState[g] === v;
        chip.setAttribute('aria-pressed', String(!!on));
      });
      if (findInput && filterState.q) findInput.value = filterState.q;
      if (eyeDisclosure && (filterState.eye || filterState.glasses)) eyeDisclosure.open = true;
    }

    $$('.lrc-chip[data-filter-group]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        exitPicks();
        var g = chip.getAttribute('data-filter-group');
        var v = chip.getAttribute('data-filter-value');
        var label = chip.getAttribute('data-filter-label');
        var isOn = chip.getAttribute('aria-pressed') === 'true';
        $$('.lrc-chip[data-filter-group="' + g + '"]').forEach(function (c2) {
          c2.setAttribute('aria-pressed', 'false');
        });
        if (g === 'glasses') filterState.glasses = null; else filterState[g] = null;
        if (!isOn) {
          chip.setAttribute('aria-pressed', 'true');
          if (g === 'glasses') filterState.glasses = '1'; else filterState[g] = v;
          lastFilterChanged = { group: g, label: label };
        }
        applyFilters();
      });
    });

    if (clearBtn) clearBtn.addEventListener('click', function () {
      exitPicks();
      filterState.style = filterState.type = filterState.eye = filterState.glasses = null;
      filterState.q = '';
      if (findInput) findInput.value = '';
      lastFilterChanged = null;
      $$('.lrc-chip[data-filter-group]').forEach(function (c2) { c2.setAttribute('aria-pressed', 'false'); });
      applyFilters();
    });

    if (zeroUndo) zeroUndo.addEventListener('click', function () {
      if (!lastFilterChanged) return;
      var g = lastFilterChanged.group;
      if (g === 'glasses') filterState.glasses = null; else filterState[g] = null;
      $$('.lrc-chip[data-filter-group="' + g + '"]').forEach(function (c2) { c2.setAttribute('aria-pressed', 'false'); });
      lastFilterChanged = null;
      applyFilters();
    });

    if (findInput) {
      var findTimer = null;
      findInput.addEventListener('input', function () {
        exitPicks();
        clearTimeout(findTimer);
        findTimer = setTimeout(function () {
          filterState.q = findInput.value.trim();
          applyFilters();
        }, 150);
      });
    }

    /* ---------- card events ---------- */

    var grid = document.getElementById('lr-grid');
    if (grid) {
      grid.addEventListener('click', function (e) {
        var li = e.target.closest('.lrc-card');
        if (!li) return;
        var card = cardByVariant[li.getAttribute('data-variant-id')];
        if (!card) return;
        if (e.target.closest('[data-lr-add]') || e.target.closest('[data-lr-plus]')) addOne(card);
        else if (e.target.closest('[data-lr-minus]')) removeOne(card);
        else if (e.target.closest('[data-lr-see-more]')) openModal(card);
      });
    }

    /* press-and-hold worn view */
    $$('[data-lr-media][data-worn]').forEach(function (media) {
      var img = $('img', media);
      if (!img) return;
      var held = false;
      function press() {
        held = true;
        img.src = media.getAttribute('data-worn');
        img.removeAttribute('srcset');
      }
      function release() {
        if (!held) return;
        held = false;
        img.src = img.getAttribute('data-main-src');
      }
      media.addEventListener('pointerdown', press);
      media.addEventListener('pointerup', release);
      media.addEventListener('pointerleave', release);
      media.addEventListener('pointercancel', release);
      media.addEventListener('contextmenu', function (e) { if (held) e.preventDefault(); });
    });

    /* ---------- see-more modal ---------- */

    var modal = $('[data-lr-modal]');
    var modalOpener = null;
    var modalCard = null;

    function setRow(el, text) {
      if (!el) return;
      el.hidden = !text;
      if (text) el.textContent = text;
    }

    function openModal(card) {
      if (!modal) return;
      var dataEl = $('[data-lr-modal-data]', card.el);
      var data = {};
      try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
      modalCard = card;
      modalOpener = document.activeElement;

      var img = $('[data-lr-modal-img]', modal);
      if (img) {
        img.src = data.worn || data.image || '';
        img.alt = data.name || '';
      }
      $('[data-lr-modal-name]', modal).textContent = data.name || '';
      var stars = '';
      if (data.raters) {
        var r = Math.max(1, Math.min(5, Math.round(parseFloat(data.rating) || 5)));
        stars = new Array(r + 1).join('★') + ' (' + data.raters + ')';
      }
      $('[data-lr-modal-stars]', modal).textContent = stars;
      $('[data-lr-modal-character]', modal).textContent = data.character || '';
      setRow($('[data-lr-modal-fit]', modal), data.fit);
      setRow($('[data-lr-modal-glasses]', modal), data.glasses);
      setRow($('[data-lr-modal-band]', modal), data.band);
      var quoteEl = $('[data-lr-modal-quote]', modal);
      if (quoteEl) {
        quoteEl.hidden = !data.quote;
        if (data.quote) quoteEl.textContent = '“' + data.quote + '”';
      }
      setRow($('[data-lr-modal-inbox]', modal), card.qty > 0 ? 'Already in your box: ' + card.qty : '');
      setRow($('[data-lr-modal-pair]', modal), data.pairs ? '✦ Layers beautifully with ' + data.pairs + '.' : '');

      var specsToggle = $('[data-lr-specs-toggle]', modal);
      var specsEl = $('[data-lr-modal-specs]', modal);
      var specsRows = [];
      if (data.curl) specsRows.push(['Curl', data.curl]);
      if (data.length) specsRows.push(['Length', data.length]);
      if (data.band) specsRows.push(['Band', data.band.split('.')[0]]);
      if (specsToggle && specsEl) {
        specsToggle.hidden = specsRows.length === 0;
        specsToggle.setAttribute('aria-expanded', 'false');
        $('[data-lr-specs-chevron]', modal).textContent = '▾';
        specsEl.hidden = true;
        specsEl.innerHTML = specsRows.map(function (row) {
          return '<dt>' + row[0] + '</dt><dd></dd>';
        }).join('');
        $$('dd', specsEl).forEach(function (dd, i) { dd.textContent = specsRows[i][1]; });
      }

      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      var x = $('.lrr-modal__x', modal);
      if (x) x.focus();
    }

    function closeModal() {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      document.body.style.overflow = '';
      modalCard = null;
      if (modalOpener && modalOpener.focus) modalOpener.focus();
    }

    if (modal) {
      $$('[data-lr-modal-close]', modal).forEach(function (el) {
        el.addEventListener('click', closeModal);
      });
      $('[data-lr-modal-add]', modal).addEventListener('click', function () {
        if (!modalCard) return;
        var card = modalCard;
        closeModal();
        addOne(card);
      });
      var specsToggleEl = $('[data-lr-specs-toggle]', modal);
      if (specsToggleEl) specsToggleEl.addEventListener('click', function () {
        var specsEl = $('[data-lr-modal-specs]', modal);
        var open = specsEl.hidden;
        specsEl.hidden = !open;
        specsToggleEl.setAttribute('aria-expanded', String(open));
        $('[data-lr-specs-chevron]', modal).textContent = open ? '▴' : '▾';
      });
    }

    /* ---------- checkout seatbelt ---------- */

    var confirmModal = $('[data-lr-confirm]');

    function openConfirm(n, total) {
      var toFull = cfg.bigCount - n;
      var f = Math.min(n / cfg.bigCount, 1);
      $('.lrr-modal__panel--confirm', confirmModal).setAttribute('aria-label', 'Almost a full moon');
      $('[data-lr-confirm-moon]', confirmModal).setAttribute('d', moonPath(f));
      $('[data-lr-confirm-title]', confirmModal).textContent =
        (toFull === 1 ? 'One' : numWord(toFull)) + (toFull === 1 ? ' lash' : ' lashes') + ' from full moon';
      var body;
      if (toFull === 1) {
        body = 'One more lash brings your total down to ' + fmt(cfg.bigPriceCents) + '. Add one more, or continue with ' + n + ' at ' + fmt(total) + '?';
      } else {
        var delta = cfg.bigPriceCents - total;
        body = delta > 0
          ? numWord(toFull) + ' more lashes cost just ' + fmt(delta) + ' more all up. Complete the Full Moon Box at ' + fmt(cfg.bigPriceCents) + ', or continue with ' + n + ' at ' + fmt(total) + '?'
          : 'Complete the Full Moon Box at ' + fmt(cfg.bigPriceCents) + ', or continue with ' + n + ' at ' + fmt(total) + '?';
      }
      $('[data-lr-confirm-body]', confirmModal).textContent = body;
      $('[data-lr-confirm-close].lrr-confirm__keep', confirmModal).textContent =
        'I’ll pick ' + (toFull === 1 ? 'one' : numWord(toFull).toLowerCase()) + ' more';
      $('[data-lr-confirm-proceed]', confirmModal).textContent = 'Continue with ' + n + ' at ' + fmt(total);
      confirmModal.hidden = false;
      document.body.style.overflow = 'hidden';
      $('.lrr-confirm__keep', confirmModal).focus();
    }

    /* Past-full seatbelt: the box holds seven; extras ride at full price.
       Primary action is trimming back, continuing is the quiet link. */
    function openOverConfirm(n, total) {
      var over = n - cfg.bigCount;
      var big = cfg.bigPriceCents;
      var sevenWord = numWord(cfg.bigCount);
      $('.lrr-modal__panel--confirm', confirmModal).setAttribute('aria-label', 'The moon is past full');
      $('[data-lr-confirm-moon]', confirmModal).setAttribute('d', moonPath(1));
      $('[data-lr-confirm-title]', confirmModal).textContent = 'The moon is past full';
      var delta = total - big;
      var body = sevenWord + ' lashes make the Full Moon Box at ' + fmt(big) + '.';
      if (delta > 0) {
        body += ' The extra ' + (over === 1 ? 'lash adds ' + fmt(delta) : numWord(over).toLowerCase() + ' add ' + fmt(delta)) + ' at full price.';
      }
      body += ' Set ' + (over === 1 ? 'it' : 'them') + ' aside, or continue with all ' + n + ' at ' + fmt(total) + '?';
      $('[data-lr-confirm-body]', confirmModal).textContent = body;
      $('[data-lr-confirm-close].lrr-confirm__keep', confirmModal).textContent =
        'I’ll trim back to ' + sevenWord.toLowerCase();
      $('[data-lr-confirm-proceed]', confirmModal).textContent = 'Continue with ' + n + ' at ' + fmt(total);
      confirmModal.hidden = false;
      document.body.style.overflow = 'hidden';
      $('.lrr-confirm__keep', confirmModal).focus();
    }

    function closeConfirm() {
      if (!confirmModal || confirmModal.hidden) return;
      confirmModal.hidden = true;
      document.body.style.overflow = '';
    }

    if (confirmModal) {
      $$('[data-lr-confirm-close]', confirmModal).forEach(function (el) {
        el.addEventListener('click', closeConfirm);
      });
    }

    if (meter.checkout) {
      meter.checkout.addEventListener('click', function (e) {
        var n = boxCount();
        var total = boxTotal();
        if (confirmModal && n > cfg.bigCount) {
          e.preventDefault();
          openOverConfirm(n, total);
        } else if (confirmModal && n > cfg.smallCount && n < cfg.bigCount && total > cfg.bigPriceCents) {
          e.preventDefault();
          openConfirm(n, total);
        } else {
          flushThenNavigate(e, meter.checkout);
        }
      });
    }

    if (confirmModal) {
      var proceedLink = $('[data-lr-confirm-proceed]', confirmModal);
      if (proceedLink) proceedLink.addEventListener('click', function (e) {
        flushThenNavigate(e, proceedLink);
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (confirmModal && !confirmModal.hidden) closeConfirm();
        else closeModal();
      }
    });

    /* ---------- second box ---------- */

    if (meter.second) {
      meter.second.addEventListener('click', function () {
        var moved = false;
        cards.forEach(function (c) {
          if (c.qty > 0) {
            banked[c.variantId] = (banked[c.variantId] || 0) + c.qty;
            c.qty = 0;
            moved = true;
          }
        });
        if (!moved) return;
        boxes += 1;
        persistBank();
        render();
      });
    }

    /* ---------- liners + rituals ---------- */

    liners.forEach(function (u) {
      var head = $('[data-lr-liner-head]', u.el);
      var options = $('[data-lr-liner-options]', u.el);
      var chev = $('[data-lr-liner-chev]', u.el);
      if (head && options) {
        head.addEventListener('click', function () {
          var open = options.hidden;
          liners.forEach(function (other) {
            var oOpt = $('[data-lr-liner-options]', other.el);
            var oHead = $('[data-lr-liner-head]', other.el);
            var oChev = $('[data-lr-liner-chev]', other.el);
            if (oOpt) oOpt.hidden = true;
            if (oHead) oHead.setAttribute('aria-expanded', 'false');
            if (oChev) oChev.textContent = '▾';
            other.el.classList.remove('is-open');
          });
          if (open) {
            options.hidden = false;
            head.setAttribute('aria-expanded', 'true');
            if (chev) chev.textContent = '▴';
            u.el.classList.add('is-open');
          }
        });
      }
    });

    liners.concat(rituals).forEach(function (u) {
      u.chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          u.chips.forEach(function (c2) { c2.setAttribute('aria-pressed', 'false'); });
          chip.setAttribute('aria-pressed', 'true');
          u.selectedId = chip.getAttribute('data-variant-id');
          u.selectedPrice = parseInt(chip.getAttribute('data-price'), 10) || 0;
          u.selectedLabel = chip.getAttribute('data-label') || '';
          renderUpsell(u);
        });
      });
      if (u.addBtn) {
        u.addBtn.addEventListener('click', function () {
          if (pending) return;
          pending = true;
          if (!u.added) {
            var addId = u.selectedId;
            enqueueCartOp(function () { return cartAdd(addId); }).then(function () {
              u.added = true;
              u.addedVariantId = addId;
              pending = false;
              renderUpsell(u);
            }).catch(function () { pending = false; showError(); });
          } else {
            var removeId = u.addedVariantId || u.selectedId;
            enqueueCartOp(function () { return cartSetQty(removeId, 0); }).then(function () {
              u.added = false;
              u.addedVariantId = null;
              pending = false;
              renderUpsell(u);
            }).catch(function () { pending = false; showError(); });
          }
        });
      }
    });

    var skip = $('[data-lr-skip]');
    if (skip) {
      skip.addEventListener('click', function () {
        var row = document.getElementById('lr-ritual-row');
        if (!row) return;
        var top = row.getBoundingClientRect().top + window.pageYOffset - 70;
        window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }

    /* ---------- init ---------- */

    if (gridSection) {
      parseHash();
      applyFilters();
      render();
      hydrate();
    } else {
      render();
      hydrate();
    }
  });
})();